import type { YMap } from "@yandex/ymaps3-types";

import type { Map } from "../hooks/useFlotiqStores/useFlotiqStores.types";

export const convertToRadians = (degree: number): number => {
  return degree * (Math.PI / 180);
};

const NORTH_EAST = 0;
const SOUTH_WEST = 1;

const LAT = 0;
const LNG = 1;

const yandexMapUtils = {
  calculateRadius: (map: YMap): number => {
    const bounds = map.bounds as number[][] | null;
    if (!bounds) {
      return 0;
    }

    const northEast = bounds[NORTH_EAST];
    const southWest = bounds[SOUTH_WEST];

    const neLng = convertToRadians(northEast[LNG]);
    const neLat = convertToRadians(northEast[LAT]);
    const swLng = convertToRadians(southWest[LNG]);
    const swLat = convertToRadians(southWest[LAT]);

    const dlon = swLng - neLng;
    const dlat = swLat - neLat;

    const haversineFormula = Math.sin(dlat / 2) ** 2 + Math.cos(neLat) * Math.cos(swLat) * Math.sin(dlon / 2) ** 2;
    const centralAngle = 2 * Math.atan2(Math.sqrt(haversineFormula), Math.sqrt(1 - haversineFormula));

    const EARTH_RADIUS_IN_KM = 6371;
    const distance = EARTH_RADIUS_IN_KM * centralAngle;
    const distanceInMeters = distance * 1000;

    return Number(distanceInMeters.toFixed(2)) / 4;
  },
  handleGetLatAndLng: (map: YMap): { lat: number; lng: number } => {
    const center = map.center;

    return {
      lat: Number(center[1].toFixed(6)),
      lng: Number(center[0].toFixed(6)),
    };
  },
  getMapZoom: (map: YMap) => {
    return map.zoom;
  },
};

const naverMapUtils = {
  calculateRadius: (map: naver.maps.Map): number => {
    const bounds = map.getBounds() as naver.maps.LatLngBounds | null;

    if (!bounds?.getNE() || !bounds?.getSW()) {
      return 0;
    }

    const northEast = bounds.getNE();
    const southWest = bounds.getSW();

    // calculation based on Haversine_formula - https://en.wikipedia.org/wiki/Haversine_formula
    // created based on https://stackoverflow.com/questions/14560999/using-the-haversine-formula-in-javascript
    const neLng = convertToRadians(northEast.lng());
    const neLat = convertToRadians(northEast.lat());
    const swLng = convertToRadians(southWest.lng());
    const swLat = convertToRadians(southWest.lat());

    const dlon = swLat - neLat;
    const dlat = neLng - swLng;

    const haversineFormula = Math.sin(dlat / 2) ** 2 + Math.cos(neLat) * Math.cos(swLat) * Math.sin(dlon / 2) ** 2;
    const centralAngle = 2 * Math.atan2(Math.sqrt(haversineFormula), Math.sqrt(1 - haversineFormula));

    const EARTH_RADIUS_IN_KM = 6371;
    const distance = EARTH_RADIUS_IN_KM * centralAngle; // distance in km
    const distanceInMeters = distance * 1000; // distance in meters

    return Number(distanceInMeters.toFixed(2)) / 2;
  },
  handleGetLatAndLng: (map: naver.maps.Map): { lat: number; lng: number } => {
    const center = map.getCenter();
    return {
      lat: Number(center.y.toFixed(6)),
      lng: Number(center.x.toFixed(6)),
    };
  },
  getMapZoom: (map: naver.maps.Map) => {
    return map.getZoom();
  },
};

const googleMapUtils = {
  calculateRadius: (map: google.maps.Map): number => {
    const bounds = map.getBounds();

    if (!bounds) {
      return 0;
    }

    const northSouthRadius = google.maps.geometry.spherical.computeDistanceBetween(
      bounds.getCenter(),
      bounds.getNorthEast()
    );

    const eastWestRadius = google.maps.geometry.spherical.computeDistanceBetween(
      bounds.getCenter(),
      bounds.getSouthWest()
    );

    const radius = Math.max(northSouthRadius, eastWestRadius);

    return Number(radius.toFixed(2));
  },
  handleGetLatAndLng: (map: google.maps.Map): { lat: number; lng: number } | null => {
    const center = getGoogleMapCenter(map);

    if (!center) {
      return null;
    }

    return {
      lat: typeof center.lat === "function" ? center.lat() : center.lat,
      lng: typeof center.lng === "function" ? center.lng() : center.lng,
    };
  },
  getMapZoom: (map: google.maps.Map) => {
    return map.getZoom() ?? 1;
  },
};

export const getLatLngAndRadius = (map?: Map): { lat: number; lng: number; radius: number; zoom: number } | null => {
  if (typeof google !== "undefined" && map instanceof google.maps.Map) {
    const { calculateRadius, handleGetLatAndLng, getMapZoom } = googleMapUtils;
    const latLng = handleGetLatAndLng(map);

    if (!latLng) {
      return null;
    }

    return { ...latLng, radius: calculateRadius(map), zoom: getMapZoom(map) };
  }
  if (typeof naver !== "undefined" && map instanceof naver.maps.Map) {
    const { calculateRadius, handleGetLatAndLng, getMapZoom } = naverMapUtils;
    return { ...handleGetLatAndLng(map), radius: calculateRadius(map), zoom: getMapZoom(map) };
  }
  if (typeof ymaps3 !== "undefined" && map instanceof ymaps3.YMap) {
    const { calculateRadius, handleGetLatAndLng, getMapZoom } = yandexMapUtils;
    return { ...handleGetLatAndLng(map), radius: calculateRadius(map), zoom: getMapZoom(map) };
  }
  return { lat: 0, lng: 0, radius: 0, zoom: 1 };
};

function getGoogleMapCenter(map: google.maps.Map): google.maps.LatLng | google.maps.LatLngLiteral | null {
  const configuredCenter = map.get("center") as google.maps.LatLng | google.maps.LatLngLiteral | undefined;

  return map.getCenter() ?? configuredCenter ?? map.getBounds()?.getCenter() ?? null;
}
